import React, { useEffect, useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

// Card entry is handled entirely by Stripe Elements (each field rendered in
// Stripe's own iframe), so raw card data never touches our code or servers.
// Stripe returns a pm_... token which we attach to the client's Stripe
// customer via the API.
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#171725",
      "::placeholder": { color: "#9ca3af" },
    },
    invalid: { color: "#dc2626" },
  },
};

function AddPaymentForm({ onAdd, onClose, isSubmitting }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardholderName, setCardholderName] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [formError, setFormError] = useState("");
  const [isTokenizing, setIsTokenizing] = useState(false);

  const isBusy = isSubmitting || isTokenizing;
  const [visibleError, setVisibleError] = useState("");

  useEffect(() => {
    if (!formError) {
      setVisibleError("");
      return;
    }

    setVisibleError(formError);
    const timeoutId = setTimeout(() => {
      setVisibleError("");
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [formError]);

  const handleAdd = async () => {
    setFormError("");

    if (!stripe || !elements) {
      setFormError("Payment form is still loading. Please try again.");
      return;
    }

    if (!cardholderName.trim()) {
      setFormError("Cardholder name is required.");
      return;
    }

    setIsTokenizing(true);
    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
      billing_details: { name: cardholderName.trim() },
    });
    setIsTokenizing(false);

    if (error) {
      setFormError(error.message || "Invalid card details.");
      return;
    }

    await onAdd(paymentMethod.id, isDefault);

    setCardholderName("");
    elements.getElement(CardNumberElement)?.clear();
    elements.getElement(CardExpiryElement)?.clear();
    elements.getElement(CardCvcElement)?.clear();
    onClose();
  };

  return (
    <>
      <div className="space-y-4">
        <div>
          <label className="text-sm text-primary mb-1 block">Card Number</label>
          <div className="border border-primary rounded-md px-3 py-3">
            <CardNumberElement options={{ ...ELEMENT_OPTIONS, showIcon: true }} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-primary mb-1 block">
              Expiration Date
            </label>
            <div className="border border-primary rounded-md px-3 py-3">
              <CardExpiryElement options={ELEMENT_OPTIONS} />
            </div>
          </div>
          <div>
            <label className="text-sm text-primary mb-1 block">
              Security Code
            </label>
            <div className="border border-primary rounded-md px-3 py-3">
              <CardCvcElement options={ELEMENT_OPTIONS} />
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm text-primary mb-1 block">Cardholder Name</label>
          <input
            className="w-full border border-primary rounded-md px-3 py-2 placeholder:text-primary text-sm sm:text-base"
            placeholder="Enter your name"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-primary">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          Set as default payment method
        </label>
      </div>

      {visibleError && <p className="mt-4 text-sm text-red-600">{visibleError}</p>}

      <div className="mt-6 flex justify-end gap-3 font-semibold">
        <button
          onClick={handleAdd}
          disabled={isBusy}
          className="bg-primary text-white text-sm sm:text-base px-4 py-2 rounded-md leading-none disabled:opacity-50"
        >
          {isBusy ? "ADDING..." : "ADD CARD"}
        </button>
        <button
          onClick={onClose}
          disabled={isBusy}
          className="border border-primary rounded-md px-4 py-2 text-primary text-sm sm:text-base leading-none"
        >
          CANCEL
        </button>
      </div>
    </>
  );
}

export default function AddPaymentModal({
  open,
  onClose,
  onAdd,
  isSubmitting = false,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center max-sm:pt-5">
      <div className="absolute inset-0 bg-black opacity-50" onClick={onClose} />
      <div className="relative w-full max-w-[520px] bg-white rounded-2xl p-8 border border-line mx-6 sm:mx-10">
        <h3 className="text-xl font-semibold mb-1">Add Payment Method</h3>
        <p className="text-sm text-mutedLight mb-4">
          Your card details are processed securely by Stripe.
        </p>

        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <AddPaymentForm
              onAdd={onAdd}
              onClose={onClose}
              isSubmitting={isSubmitting}
            />
          </Elements>
        ) : (
          <p className="text-sm text-red-600">
            Payments are not configured (missing Stripe publishable key).
          </p>
        )}
      </div>
    </div>
  );
}
